import {
  Body,
  Controller,
  DatePicker,
  FormProvider,
  Input,
  Link,
  MaterialIcon,
  Stack,
  Tooltip,
  useForm,
} from '@qred/components-library';
import dayjs from 'dayjs';
import { useFeatureFlagVariantKey } from 'posthog-js/react';
import React, { useContext, useEffect, useState } from 'react';
import { FieldPath } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { ValidationContext } from '~/components/hoc/withValidation';
import { AddressSearch } from '~/components/shared/AddressSearch/AddressSearch';
import { FormErrorBanner } from '~/components/shared/FormErrorBanner/FormErrorBanner';
import {
  NLAddressLookup,
  NLAddressLookupSchema,
} from '~/components/shared/NLAddressLookup/NLAddressLookup';
import NextButton from '~/components/shared/Stepper/components/NextButton';
import StepHeader from '~/components/shared/Stepper/components/StepHeader';
import TrustpilotWidget from '~/components/shared/TrustpilotWidget/TrustpilotWidget';
import {
  getMarketHasCreditCheckConsentDisclosure,
  getPersonalNumberPlaceholder,
  marketHasAddress,
  marketHasDateOfBirth,
  marketHasPersonalNumber,
  marketHasTrustpilot,
  marketShouldAskLoanApplicantForFirstAndLastName,
  marketShouldShowCreditCheckCopyTooltip,
  personalNumberIsOnlyNumbers,
  phoneNumberCountryCodes,
} from '~/constants/markets';
import { CountryCode } from '~/enums';
import {
  normalizePersonalNumberForBackend,
  removeWhitespace,
} from '~/helpers/formatters.helper';
import { buildStreetValue } from '~/helpers/shared.helper';
import { PostcodeAddress } from '~/hooks/usePostcode';
import useTranslate from '~/hooks/useTranslate';
import { ILoanApplicationApplicant } from '~/interfaces/loanApplication';
import { RootState } from '~/store';
import { pushToGtmLoanAction } from '~/store/actions/gtmActions';
import { updateApplicantData } from '~/store/slices/loanApplication.slice';
import { ApiStatus } from '~/store/types/sharedTypes';
import {
  StandardStepContent,
  StandardStepContentBody,
  StandardStepContentFooter,
} from '~/styles/BaseStyledComponents';
import { LastBasicStepSummaryBox } from '../Steps/LastBasicStepSummaryBox';
import useGoToNextStep from '../Steps/useGoToNextStep';
import useStepConfig from '../Steps/useStepConfig';
import {
  createPersonalInformationSchema,
  createPersonalNumberSchema,
  dateOfBirthSchema,
  DEAddressSchema,
  firstAndLastNameSchema,
  PersonalInformationForm,
} from './PersonalInformation.schema';

const DEFAULT_DATE_OF_BIRTH = new Date(
  new Date().setFullYear(new Date().getFullYear() - 18)
);

export const PersonalInformation = () => {
  const t = useTranslate();
  const {
    intl: { market, language },
    loanApplication: {
      form: {
        applicant: {
          firstName,
          lastName,
          dateOfBirth,
          phone,
          email,
          address,
          addressFields,
          personalNumber,
        },
      },
      apiStatus: applicationApiStatus,
    },
  } = useSelector((state: RootState) => state);
  const { isLastStep, isLastBasicStep } = useStepConfig();
  const onNextStepClick = useGoToNextStep();
  const indicativeOfferVariant = useFeatureFlagVariantKey(
    'AB_Web_LoanApplication_IndicativeOffer_MoreSuperApplications_SE_FI'
  );

  const initialDEAddressSearchValue = address
    ? [address.street, address.zipCode, address.city].join(', ')
    : undefined;
  const [DEAddressFieldValue, setDEAddressFieldValue] = useState(
    initialDEAddressSearchValue
  );

  const getInitialAddress = () => {
    return market === CountryCode.NL ? addressFields : address;
  };

  const personalInformationSchema = createPersonalInformationSchema(market);

  const formMethods = useForm<PersonalInformationForm>({
    schema: personalInformationSchema,
    defaultValues: {
      firstName: marketShouldAskLoanApplicantForFirstAndLastName[market]
        ? firstName
        : undefined,
      lastName: marketShouldAskLoanApplicantForFirstAndLastName[market]
        ? lastName
        : undefined,
      dateOfBirth:
        marketHasDateOfBirth[market] && dateOfBirth
          ? new Date(dateOfBirth)
          : undefined,
      personalNumber: marketHasPersonalNumber[market]
        ? personalNumber || ''
        : undefined,
      phone: phone || phoneNumberCountryCodes[market],
      email: email || '',
      address: marketHasAddress[market] ? getInitialAddress() : undefined,
    },
    mode: 'onTouched',
  });
  const {
    setValue,
    watch,
    register,
    control,
    formState: { errors, isValid, isValidating },
  } = formMethods;

  const fieldValues = watch();

  const dispatch = useDispatch();
  const validationContext = useContext(ValidationContext);

  const handleSelectedAddress = (selectedAddress: PostcodeAddress | null) => {
    if (selectedAddress) {
      const {
        locality,
        postcode,
        street,
        buildingNumber,
        buildingNumberAddition,
      } = selectedAddress.address;

      const fullStreet = `${street} ${buildingNumber || ''}${
        buildingNumberAddition || ''
      }`.trim();

      setValue(
        'address',
        {
          city: locality,
          street: fullStreet,
          zipCode: postcode,
        },
        { shouldValidate: true }
      );
    }
  };

  const onAddressSearchChange = (value: string) => {
    setValue('address', undefined, { shouldValidate: true });
    setDEAddressFieldValue(value);
  };

  const onBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const trimmedValue = value.trim();
    setValue(name as FieldPath<PersonalInformationForm>, trimmedValue);
    dispatch(
      pushToGtmLoanAction({
        actionName: `${name}_change`,
      })
    );
  };

  useEffect(() => {
    if (isValid && !isValidating) {
      const updatedForm: Partial<ILoanApplicationApplicant> = {
        phone: fieldValues.phone,
        email: fieldValues.email,
      };

      if (marketShouldAskLoanApplicantForFirstAndLastName[market]) {
        const firstAndLastNameForm = firstAndLastNameSchema.parse(fieldValues);
        updatedForm.firstName = firstAndLastNameForm.firstName;
        updatedForm.lastName = firstAndLastNameForm.lastName;
      }

      if (marketHasDateOfBirth[market]) {
        const dateOfBirthValue = dateOfBirthSchema.parse(
          fieldValues.dateOfBirth
        );

        updatedForm.dateOfBirth = dayjs(dateOfBirthValue).format('YYYY-MM-DD');
      }

      if (marketHasPersonalNumber[market]) {
        const personalNumberValue = createPersonalNumberSchema(market).parse(
          fieldValues.personalNumber
        );
        updatedForm.personalNumber = personalNumberValue;
      }

      if (marketHasAddress[market]) {
        if (market === CountryCode.DE) {
          const DEFieldValues = DEAddressSchema.parse(fieldValues.address);
          updatedForm.address = {
            city: DEFieldValues.city,
            street: DEFieldValues.street,
            zipCode: DEFieldValues.zipCode,
          };
        }

        if (market === CountryCode.NL) {
          const NLFieldValues = NLAddressLookupSchema.parse(fieldValues);
          updatedForm.addressFields = {
            zipCode: NLFieldValues.address.zipCode,
            houseNumber: NLFieldValues.address.houseNumber,
            houseNumberAddition: NLFieldValues.address.houseNumberAddition,
            city: NLFieldValues.address.city,
            streetName: NLFieldValues.address.streetName,
          };
          updatedForm.address = {
            city: NLFieldValues.address.city,
            street: buildStreetValue({
              ...NLFieldValues.address,
            }),
            zipCode: removeWhitespace(NLFieldValues.address.zipCode),
          };
        }
      }

      dispatch(updateApplicantData(updatedForm));
    } else {
      validationContext.addPropertyToValidationErrors(
        'personalInformationStep'
      );
    }

    return () => {
      validationContext.removePropertyFromValidationErrors(
        'personalInformationStep'
      );
    };
  }, [isValid, isValidating, market, dispatch]);

  let nextButtonLabel = 'Onboarding.Next';
  if (isLastStep || isLastBasicStep) {
    nextButtonLabel =
      indicativeOfferVariant === 'test'
        ? 'Onboarding.SubmitAndViewIndicativeOffer'
        : 'Onboarding.Submit';
  }

  return (
    <StandardStepContent>
      <StandardStepContentBody>
        <StepHeader
          title="Onboarding.PersonalInformationTitle"
          description="Onboarding.PersonalInformationDescription"
        />
        <FormProvider {...formMethods}>
          <Stack spacing={'xs'}>
            <Input.Group orientation="vertical">
              {marketShouldAskLoanApplicantForFirstAndLastName[market] && (
                <>
                  <Input
                    {...register('firstName', { onBlur })}
                    label={t('FirstName')}
                    error={
                      errors?.firstName?.message &&
                      t(errors?.firstName?.message)
                    }
                    id="firstName"
                  />
                  <Input
                    {...register('lastName', { onBlur })}
                    label={t('LastName')}
                    error={
                      errors?.lastName?.message && t(errors?.lastName?.message)
                    }
                    id="lastName"
                  />
                </>
              )}

              <Input
                {...register('phone', { onBlur })}
                label={t('MobilePhone')}
                error={errors?.phone?.message && t(errors?.phone?.message)}
                id="phone"
              />

              <Input
                {...register('email', { onBlur })}
                label={t('Email')}
                error={errors?.email?.message && t(errors?.email?.message)}
                id="email"
              />

              {marketHasDateOfBirth[market] && (
                <Controller
                  name="dateOfBirth"
                  control={control}
                  render={({ field, fieldState }) => (
                    <DatePicker
                      label={t('DateOfBirth')}
                      value={field.value}
                      onChange={field.onChange}
                      onBlur={() => {
                        field.onBlur();
                        dispatch(
                          pushToGtmLoanAction({
                            actionName: 'dateOfBirth_change',
                          })
                        );
                      }}
                      error={
                        fieldState.error?.message &&
                        t(fieldState.error?.message)
                      }
                      valueFormat="DD-MM-YYYY"
                      defaultDate={DEFAULT_DATE_OF_BIRTH}
                      maxDate={new Date()}
                      id="dateOfBirth"
                    />
                  )}
                />
              )}
              {marketHasPersonalNumber[market] && (
                <Input
                  {...register('personalNumber', {
                    setValueAs: (value) =>
                      normalizePersonalNumberForBackend(value || '', market),
                    onBlur,
                  })}
                  placeholder={getPersonalNumberPlaceholder(market, language)}
                  label={t('PersonalNumber')}
                  error={
                    errors?.personalNumber?.message &&
                    t(errors?.personalNumber?.message)
                  }
                  inputMode={
                    personalNumberIsOnlyNumbers(market) ? 'numeric' : 'text'
                  }
                  rightSection={
                    marketShouldShowCreditCheckCopyTooltip[market] ? (
                      <Tooltip
                        label={t(
                          'Onboarding.TooltipCreditCheckReportGenerated',
                          {
                            TooltipCreditCheckImpactWorthiness: (
                              <Body size="md" weight={600}>
                                {t(
                                  'Onboarding.TooltipCreditCheckImpactWorthiness'
                                )}
                              </Body>
                            ),
                          }
                        )}
                      >
                        <MaterialIcon src="help" size={'24'} />
                      </Tooltip>
                    ) : undefined
                  }
                  id="personalNumber"
                />
              )}

              {marketHasAddress[market] && (
                <>
                  {market === CountryCode.DE && (
                    <Controller
                      name="address"
                      control={control}
                      render={({ field, fieldState }) => (
                        <AddressSearch
                          label={t('Onboarding.ApplicantAddress')}
                          handleResult={handleSelectedAddress}
                          onChange={onAddressSearchChange}
                          onBlur={() => {
                            field.onBlur();
                            dispatch(
                              pushToGtmLoanAction({
                                actionName: 'address_change',
                              })
                            );
                          }}
                          error={
                            fieldState.error?.message &&
                            t(fieldState.error?.message)
                          }
                          initialValue={DEAddressFieldValue}
                        />
                      )}
                    />
                  )}

                  {market === CountryCode.NL && <NLAddressLookup />}
                </>
              )}
            </Input.Group>
            <FormErrorBanner errors={errors} />
          </Stack>
        </FormProvider>
        {isValid && isLastBasicStep && <LastBasicStepSummaryBox />}
      </StandardStepContentBody>
      <StandardStepContentFooter>
        {getMarketHasCreditCheckConsentDisclosure(market) && (
          <Body color="Text/Secondary" size="sm" display="block" align="left">
            {t('Onboarding.MarketingConsent', {
              next: t('Onboarding.Next'),
              email: (
                <a href="mailto:privacy@qred.com">
                  <Link size="sm">privacy@qred.com</Link>
                </a>
              ),
            })}
          </Body>
        )}
        <NextButton
          label={nextButtonLabel}
          isLoading={applicationApiStatus.patch === ApiStatus.Started}
          action={onNextStepClick}
        />
        {isLastBasicStep && (
          <Stack spacing={'sm'}>
            {marketHasTrustpilot[market] && (
              <TrustpilotWidget templateId="5419b637fa0340045cd0c936" />
            )}
            <Body size="sm" display="block">
              {t('Onboarding.ApplicationProcessingConsent')}
            </Body>
          </Stack>
        )}
      </StandardStepContentFooter>
    </StandardStepContent>
  );
};
